import { useCallback } from 'react';
import { WorkspaceMapDto } from '@mark43/cad-api';
import { isValidLatLng } from '../../location/helpers';
import { ESRI_MAP_DEFAULT_ZOOM_LEVEL } from '../../esri/configurations';
import { useEsriMapStore } from './use-esri-map-store';
import { useEsriMapViewRef } from './use-esri-map-view-ref';
import {
    initializeMapFilterStateFromWorkspaceMapDto,
    initializeMapLayerIdsFromWorkspaceMapDto,
} from '../components/helpers';

export const useResetWorkspaceMapView = () => {
    const mapStore = useEsriMapStore();
    const mapViewRef = useEsriMapViewRef();

    const resetWorkspaceMapView = useCallback(
        (workspaceMapDto?: WorkspaceMapDto) => {
            if (!workspaceMapDto) {
                return;
            }

            mapStore.setMapFilterState(
                initializeMapFilterStateFromWorkspaceMapDto(workspaceMapDto)
            );
            mapStore.setVisibleLayersIds(initializeMapLayerIdsFromWorkspaceMapDto(workspaceMapDto));

            const { zoomLevel, centerLatitude, centerLongitude } = workspaceMapDto;
            const resetToZoomLevel = zoomLevel ?? ESRI_MAP_DEFAULT_ZOOM_LEVEL;
            mapStore.setMapZoomLevel(resetToZoomLevel);
            mapStore.setMapZoomForUpdate(resetToZoomLevel);

            // map center from bootstrap, it will not change after bootstrap load.
            const bootstrapMapCenter = mapStore.mapBootstrapState?.mapCenter;
            const resetToMapCenter = isValidLatLng(centerLatitude, centerLongitude)
                ? { latitude: centerLatitude!, longitude: centerLongitude! }
                : bootstrapMapCenter;
            // mapCenter is for the coordinate when click reset map. this may change as user update their default map view
            mapStore.setMapCenter(resetToMapCenter);
            // mapCenterForUpdate center of current map view. it will keep changing while map view is moving
            mapStore.setMapCenterForUpdate(resetToMapCenter);

            if (mapViewRef.current) {
                if (resetToMapCenter) {
                    mapViewRef.current.goTo?.({
                        center: [resetToMapCenter.longitude, resetToMapCenter.latitude],
                        zoom: resetToZoomLevel,
                    });
                } else {
                    mapViewRef.current.goTo?.({
                        zoom: resetToZoomLevel,
                    });
                }
            }

            // fresh map entities based on updated filters
            mapStore.updateMapView();
        },
        [mapStore, mapViewRef]
    );
    return resetWorkspaceMapView;
};
